import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import Login from "./routes/Login";
import MapPage from "./routes/MapPage";
import SetPassword from "./routes/SetPassword";
import MobileShell from "./m/MobileShell";
import { homePath } from "./lib/pwa";
import ResetPassword from "./routes/ResetPassword";
import { onSetPasswordChange, pendingSetPassword } from "./lib/setpw";
import AdminPage from "./routes/admin/AdminPage";
import ReportsPage from "./routes/ReportsPage";
import SharePage from "./routes/SharePage";
import FleetPage from "./routes/FleetPage";
import CompanyPage from "./routes/CompanyPage";
import { usePresenceHeartbeat } from "./lib/presence";
import SupportBanner from "./ui/SupportBanner";
import { StatusBanner } from "./ui/StatusNotice";
import StatusKeyPage from "./routes/StatusKeyPage";

// Invite and reset links land on /set-password#...&type=invite|recovery. supabase-js clears
// the hash once it has read the session, so remember the link type before that happens.
const LINK_TYPE = new URLSearchParams(window.location.hash.slice(1)).get("type");

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const { pathname } = useLocation();
  const [setPw, setSetPw] = useState(pendingSetPassword);          // V3.5.1: a reset/invite code was just verified
  useEffect(() => onSetPasswordChange(() => setSetPw(pendingSetPassword())), []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);
  // V3.3.1: heartbeat for "who's online" (signed-in pages only; not on public share links)
  const isShare = window.location.pathname.startsWith("/s/");
  usePresenceHeartbeat(!isShare && session && !session.user.user_metadata?.must_change_password ? session.user.id : null);

  // Public share links work without signing in (and don't wait for the session check)
  if (window.location.pathname.startsWith("/s/")) return <Routes><Route path="/s/:token" element={<SharePage />} /></Routes>;
  // V3.11.0: break-glass notices page — no sign-in, no Supabase (works when the database or sign-in is down)
  if (pathname === "/status-admin") return <StatusKeyPage />;
  // V3.11.0: platform notice banner (from Netlify, not Supabase) — every page except the sign-in page, which shows its own box
  const banner = pathname !== "/login" && <StatusBanner mode={pathname === "/m" || pathname.startsWith("/m/") ? "phone" : pathname.startsWith("/map") ? "float" : "strip"} />;
  if (session === undefined) return <>{banner}<div className="boot">Loading…</div></>;

  const mustChange = !!session?.user.user_metadata?.must_change_password || (!!session && !!setPw);
  const linkReason = setPw ?? (LINK_TYPE === "invite" ? "invite" : "reset");
  const tempPw = !!session?.user.user_metadata?.must_change_password;

  return (<>
    {session && !mustChange && <SupportBanner />}
    {banner}
    <Routes>
      <Route path="/set-password" element={<SetPassword session={session} reason={tempPw && !setPw ? "temporary" : linkReason} />} />
      <Route path="/reset" element={session && !setPw ? <Navigate to={homePath()} replace /> : <ResetPassword />} />
      {session && mustChange ? (
        <Route path="*" element={<Navigate to="/set-password" replace />} />
      ) : (
        <>
          <Route path="/login" element={session ? <Navigate to={homePath()} replace /> : <Login />} />
          <Route path="/map" element={session ? <MapPage session={session} /> : <Navigate to="/login" replace />} />
          <Route path="/reports" element={session ? <ReportsPage session={session} /> : <Navigate to="/login" replace />} />
          <Route path="/company" element={session ? <CompanyPage session={session} /> : <Navigate to="/login" replace />} />
          <Route path="/fleet" element={session ? <FleetPage session={session} /> : <Navigate to="/login" replace />} />
          {/* V3.7.0 phone app (installable; manifest start_url /m) */}
          <Route path="/m/*" element={session ? <MobileShell session={session} /> : <Navigate to="/login" replace />} />
          <Route path="/admin/*" element={session ? <AdminPage session={session} /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to={session ? homePath() : "/login"} replace />} />
        </>
      )}
    </Routes>
  </>);
}
